import type { FunctionComponent } from 'react'
import { Heading, Modal, TypographyV2 as Typography } from '@which/seatbelt'

// @ts-expect-error
import { LoginLink } from '@kinde-oss/kinde-auth-react/components'
import classnames from 'classnames'

import { Link } from '../../../../shared/components/Link'
import { phonesLines } from '../../../../shared/constants/msc-opening-times'
import style from './ErrorModal.module.scss'

export const ErrorModal: FunctionComponent<Props> = ({
  httpErrorCode,
  showModal,
  setShowModal,
  emailAddress,
  hrefLink,
  loginPage,
}) => {
  if (!showModal) {
    return null
  }

  const supportParagraph = (
    <Typography textStyle="sb-text-body-x-small-regular">
      If the problem persists, please contact Which? member services on{' '}
      <Link href="tel:+442922670000">029 2267 0000</Link> to sign up ({phonesLines.weekdays};{' '}
      {phonesLines.weekends}; Standard call rates apply) or email{' '}
      <Link href="mailto:support@which.co.uk">support@which.co.uk</Link>
    </Typography>
  )

  const renderIntro = (introText) => {
    return (
      <>
        <Heading headingTag="h3" heading={introText} headingType="medium" />
      </>
    )
  }

  const tryAgain = (
    <Typography textStyle="sb-text-body-x-small-regular">Please try again.</Typography>
  )

  const modalContent = (errorCode: string) => {
    switch (errorCode) {
      case '202':
        return (
          <>
            <Heading headingTag="h3" heading="Your password has been reset" headingType="medium" />
            <Typography textStyle="sb-text-body-x-small-regular">
              In order to secure your account we have sent a password reset email to the inbox for{' '}
              {emailAddress}.
            </Typography>
            <Typography textStyle="sb-text-body-x-small-regular">
              Please use the link in that email to set a new secure password for your account, log
              in, then return here to sign up.
            </Typography>
          </>
        )

      case '409':
        return (
          <>
            {renderIntro("There's already an account with this email")}
            <Typography textStyle="sb-text-body-small-regular">
              You already have an account. Please{' '}
              <Link href="/login" textStyle="sb-text-body-small-regular">
                log in
              </Link>{' '}
              or{' '}
              <Link href="/forgot-password" textStyle="sb-text-body-small-regular">
                reset your password
              </Link>{' '}
              to proceed.
            </Typography>
          </>
        )
      case '422':
        return (
          <>
            {renderIntro("There's already an account with this email")}
            <Typography textStyle="sb-text-body-small-regular">
              You already have an account. Please{' '}
              <Link href="/login" textStyle="sb-text-body-small-regular">
                log in
              </Link>{' '}
              to proceed.
            </Typography>
          </>
        )
      case '408':
        return (
          <>
            {renderIntro('This is taking longer than usual...')}
            <Typography textStyle="sb-text-body-small-regular">
              It looks like the page is still loading, we don't want to keep you waiting{' '}
              {loginPage ? (
                <LoginLink>
                  <p className={classnames(style.errorModalLoginLink, 'sb-link-primary')}>
                    <span className="sb-link-animation-wrapper">click here to login.</span>
                  </p>
                </LoginLink>
              ) : (
                <Link href={hrefLink || '/'} textStyle="sb-text-body-small-regular">
                  click here to continue.
                </Link>
              )}
            </Typography>
          </>
        )

      default:
        // 400,500,504'
        return (
          <>
            {renderIntro('Sorry, a server error occurred')}
            {tryAgain}
            {supportParagraph}
          </>
        )
    }
  }

  return (
    <Modal closeModal={() => setShowModal(false)} className={style.errorModal}>
      <div className={style.errorModalContent}>{modalContent(httpErrorCode)}</div>
    </Modal>
  )
}

///////// IMPLEMENTATION /////////
type Props = {
  httpErrorCode: string
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  emailAddress?: string
  hrefLink?: string
  loginPage?: boolean
}
